import { config, useTransition } from 'react-spring';
import { UseMenuAnimationProps } from './interfaces';

/**
 * Animates mobile category levels according to forward or backward navigation.
 *
 * ---
 * 🧠 **Context**
 *
 * - Uses the current menu level as the spring transition item.
 * - Backward navigation reverses the horizontal enter and leave directions.
 *
 * ---
 * @example
 * ```ts
 * const { transitions } = useMenuAnimation({ currentView: 'categories', direction: 'forward' });
 * ```
 *
 * ---
 * @function useMenuAnimation
 * @param {UseMenuAnimationProps} props - Current navigation level and movement direction.
 * @returns {ReturnType<typeof useMenuAnimation>} Transition renderer for the active mobile menu level.
 */
export function useMenuAnimation({ currentView, direction }: UseMenuAnimationProps) {
	const transitions = useTransition(currentView, {
		from: {
			transform: direction === 'forward' ? 'translateX(100%)' : 'translateX(-100%)',
			opacity: 0,
		},
		enter: { transform: 'translateX(0%)', opacity: 1 },
		leave: {
			transform: direction === 'forward' ? 'translateX(-100%)' : 'translateX(100%)',
			opacity: 0,
		},
		config: config.default,
		initial: { transform: 'translateX(0%)', opacity: 1 },
	});

	return {
		transitions,
	};
}
